/**
 * /guest/listings/:id — full detail for one shared listing.
 *
 * A trimmed sibling of pages/ListingDetail rather than a reuse of it: that page
 * is built around an authenticated member (favorites, interest chat, deal
 * scoring, unlock/offer flows, buy-box fit), none of which a guest has. What
 * remains here is the listing itself plus the advisor to talk to.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Ban, BadgeCheck, Building2, Calendar, CheckCircle, DollarSign,
  Globe, Mail, MapPin, Phone, Sparkles, TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import GuestShell, { GuestWatermark } from "@/components/GuestShell";
import { PagePanel, PageShell } from "@/components/interior-page";
import { Button } from "@/components/ui/button";
import Loader from "@/components/ui/loader";
import { useGuest } from "@/contexts/GuestContext";
import { assetUrl, ApiError } from "@/lib/api";
import { guestApi } from "@/lib/guestApi";
import { formatPhone } from "@/lib/utils";
import type { GuestListingDetail } from "@/types";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function money(value: number | null, code: string | null): string {
  if (value == null) return "—";
  const prefix = code && code !== "USD" ? `${code} ` : "$";
  return `${prefix}${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl border border-border/75 bg-card p-4">
      <div className="flex items-center gap-2 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-2 text-xl font-extrabold tracking-tight text-foreground">{value}</div>
    </div>
  );
}

export default function GuestListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isVerified, email } = useGuest();
  const [listing, setListing] = useState<GuestListingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);

  useEffect(() => {
    if (!id || !isVerified) return;
    let cancelled = false;
    setLoading(true);
    guestApi
      .get<GuestListingDetail>(`/guest/listings/${id}`)
      .then((data) => {
        if (!cancelled) setListing(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError({
          status: err instanceof ApiError ? err.status : 0,
          message: err instanceof ApiError ? err.message : "We couldn't load this listing.",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isVerified]);

  if (!isVerified) return <Navigate to="/" replace />;

  const established = listing?.established_year
    ? [listing.established_month ? MONTHS[listing.established_month - 1] : null, listing.established_year]
        .filter(Boolean)
        .join(" ")
    : null;
  const place = listing
    ? [listing.city_name, listing.state_name, listing.country_name].filter(Boolean).join(", ")
    : null;
  const image = assetUrl(listing?.listing_image_url);

  return (
    <GuestShell>
      <PageShell className="mx-auto max-w-5xl">
        <Link
          to="/guest/listings"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          All listings shared with you
        </Link>

        {loading ? (
          <Loader className="min-h-[50vh]" />
        ) : error ? (
          <PagePanel className="text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <Ban className="h-7 w-7" />
            </div>
            <h1 className="mt-5 text-xl font-bold text-foreground">
              {/* 410 = the share is fine but the listing is gone; 404 = the share
                  itself was revoked. Two different stories for the recipient. */}
              {error.status === 410 ? "This listing is no longer available" : "You can't view this listing"}
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">{error.message}</p>
            <Button asChild className="mt-6">
              <Link to="/guest/listings">Back to your listings</Link>
            </Button>
          </PagePanel>
        ) : listing ? (
          <div className="relative">
            <GuestWatermark email={email} />

            <div className="relative z-10 space-y-6">
              {/* Header */}
              <PagePanel className="overflow-hidden p-0">
                <div className="relative h-52 w-full bg-muted/50 sm:h-64">
                  {image ? (
                    <img src={image} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">
                      <Building2 className="h-12 w-12" />
                    </div>
                  )}
                </div>
                <div className="p-5 sm:p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    {listing.category_name && (
                      <span className="rounded-full bg-primary/12 px-3 py-1 text-xs font-bold text-primary">
                        {listing.category_name}
                      </span>
                    )}
                    {listing.reference_code && (
                      <span className="rounded-full bg-muted px-3 py-1 font-mono text-xs font-semibold text-muted-foreground">
                        {listing.reference_code}
                      </span>
                    )}
                    {listing.is_sba_approved && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                        <CheckCircle className="h-3.5 w-3.5" />
                        SBA pre-qualified
                      </span>
                    )}
                    {listing.seller_financing && (
                      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
                        Seller financing
                      </span>
                    )}
                  </div>

                  <h1 className="mt-3 font-display text-2xl leading-tight text-foreground sm:text-3xl">
                    {listing.title}
                  </h1>

                  {place && (
                    <div className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4 shrink-0" />
                      {place}
                      {listing.address_line ? ` · ${listing.address_line}` : ""}
                      {listing.zip_code ? ` ${listing.zip_code}` : ""}
                    </div>
                  )}

                  {listing.shared_by && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Shared with you by <span className="font-semibold text-foreground">{listing.shared_by}</span>
                    </p>
                  )}
                </div>
              </PagePanel>

              {/* Financials */}
              <div className="grid gap-3 sm:grid-cols-3">
                <Stat
                  icon={DollarSign}
                  label="Asking price"
                  value={listing.make_offer ? "Make an offer" : money(listing.asking_price, listing.currency_code)}
                />
                <Stat icon={TrendingUp} label="Revenue" value={money(listing.revenue, listing.currency_code)} />
                <Stat icon={Building2} label="EBITDA" value={money(listing.ebitda, listing.currency_code)} />
              </div>

              {/* Narrative */}
              {(listing.overview || listing.additional_notes) && (
                <PagePanel>
                  <h2 className="text-lg font-bold tracking-tight text-foreground">Business overview</h2>
                  {listing.overview && (
                    <p className="mt-3 whitespace-pre-line text-sm leading-7 text-muted-foreground">
                      {listing.overview}
                    </p>
                  )}
                  {listing.additional_notes && (
                    <>
                      <h3 className="mt-6 text-sm font-bold uppercase tracking-[0.14em] text-muted-foreground">
                        Additional notes
                      </h3>
                      <p className="mt-2 whitespace-pre-line text-sm leading-7 text-muted-foreground">
                        {listing.additional_notes}
                      </p>
                    </>
                  )}
                </PagePanel>
              )}

              {/* Details */}
              <PagePanel>
                <h2 className="text-lg font-bold tracking-tight text-foreground">Details</h2>
                <dl className="mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                  {established && (
                    <div className="flex items-center justify-between gap-4 border-b border-border/50 pb-3">
                      <dt className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        Established
                      </dt>
                      <dd className="text-sm font-semibold text-foreground">{established}</dd>
                    </div>
                  )}
                  {listing.website_url && (
                    <div className="flex items-center justify-between gap-4 border-b border-border/50 pb-3">
                      <dt className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Globe className="h-4 w-4" />
                        Website
                      </dt>
                      <dd className="truncate text-sm font-semibold text-primary">
                        <a href={listing.website_url} target="_blank" rel="noreferrer noopener">
                          {listing.website_url}
                        </a>
                      </dd>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4 border-b border-border/50 pb-3">
                    <dt className="text-sm text-muted-foreground">Listed</dt>
                    <dd className="text-sm font-semibold text-foreground">
                      {new Date(listing.published_at || listing.created_at).toLocaleDateString()}
                    </dd>
                  </div>
                </dl>
              </PagePanel>

              {/* Advisor contact — the point of the share */}
              {listing.agent_name && (
                <PagePanel>
                  <h2 className="text-lg font-bold tracking-tight text-foreground">Your point of contact</h2>
                  <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/12 text-primary">
                      {assetUrl(listing.agent_avatar_url) ? (
                        <img src={assetUrl(listing.agent_avatar_url)!} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Building2 className="h-6 w-6" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-foreground">{listing.agent_name}</span>
                        {listing.agent_verified && <BadgeCheck className="h-4 w-4 shrink-0 text-primary" />}
                      </div>
                      {listing.agent_agency_name && (
                        <div className="text-sm text-muted-foreground">{listing.agent_agency_name}</div>
                      )}
                      {listing.agent_bio && (
                        <p className="mt-2 line-clamp-3 text-sm leading-6 text-muted-foreground">
                          {listing.agent_bio}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      {listing.agent_email && (
                        <Button asChild variant="outline" size="sm">
                          <a href={`mailto:${listing.agent_email}`}>
                            <Mail className="h-4 w-4" />
                            Email
                          </a>
                        </Button>
                      )}
                      {listing.agent_phone && (
                        <Button asChild variant="outline" size="sm">
                          <a href={`tel:${listing.agent_phone}`}>
                            <Phone className="h-4 w-4" />
                            {formatPhone(listing.agent_phone)}
                          </a>
                        </Button>
                      )}
                    </div>
                  </div>
                </PagePanel>
              )}

              {/* Conversion */}
              <PagePanel className="border-primary/25 bg-primary/[0.04]">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary/12 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-primary">
                      <Sparkles className="h-3.5 w-3.5" />
                      Want to see more like this?
                    </div>
                    <p className="mt-3 max-w-xl text-sm leading-7 text-muted-foreground">
                      Create a free BusinessLocating account to browse the full marketplace, track
                      deals, and get alerted when a matching business is listed.
                    </p>
                  </div>
                  <Button asChild size="lg" className="shrink-0">
                    <Link to="/register">
                      Join free
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </PagePanel>
            </div>
          </div>
        ) : null}
      </PageShell>
    </GuestShell>
  );
}
